import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, Mail, ArrowRight, ExternalLink, Clock } from "lucide-react";
import { getArticle, getCategory, getArticlesByCategory, AUTHOR_SLUGS } from "@/data/resources";
import { TEAM, FIRM } from "@/data/content";
import { Breadcrumbs, FadeIn } from "@/components/Shared";

function Block({ block }) {
  if (block.t === "h2")
    return <h2 className="brand-heading mt-10 text-2xl">{block.x}</h2>;
  if (block.t === "ul")
    return (
      <ul className="mt-4 space-y-2.5">
        {block.x.map((item, i) => (
          <li key={i} className="flex items-start gap-3 leading-relaxed text-slate-600">
            <span className="mt-2.5 size-1.5 shrink-0 bg-brand-gold" />
            {item}
          </li>
        ))}
      </ul>
    );
  return <p className="mt-4 leading-relaxed text-slate-600">{block.x}</p>;
}

export default function Article() {
  const { category, slug } = useParams();
  const article = getArticle(category, slug);
  if (!article) return <Navigate to={`/resources/${category || ""}`} replace />;

  const cat = getCategory(category);
  const authorSlug = AUTHOR_SLUGS[article.author];
  const authorPerson = TEAM.find((p) => p.slug === authorSlug);
  const related = getArticlesByCategory(category).filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <main data-testid="article-page">
      {/* Article hero */}
      <section className="brand-panel-dark pb-16 pt-36 sm:pb-20 sm:pt-44">
        <div className="brand-container">
          <FadeIn>
            <p className="brand-eyebrow-on-dark">
              {cat.title}
            </p>
            <h1 className="mt-4 max-w-4xl text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-6xl" data-testid="article-title">
              {article.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-300">
              <span>By {article.author}</span>
              <span>{article.date}</span>
              <span className="flex items-center gap-1.5">
                <Clock className="size-3.5" /> {article.readTime}
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      <Breadcrumbs
        items={[
          { label: "Legal Resources", href: "/resources" },
          { label: cat.title, href: `/resources/${category}` },
          { label: article.title },
        ]}
      />

      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Article body */}
            <div className="lg:col-span-8">
              <FadeIn>
                {article.standfirst && (
                  <p className="border-l-2 border-brand-gold pl-6 text-xl font-light leading-relaxed text-slate-700" data-testid="article-standfirst">
                    {article.standfirst}
                  </p>
                )}
                <div className="mt-6" data-testid="article-body">
                  {article.body.map((block, i) => (
                    <Block key={i} block={block} />
                  ))}
                </div>
                <p className="mt-12 border-t border-slate-200 pt-6 text-xs leading-relaxed text-slate-400">
                  Legal disclaimer: Articles are intended as an introduction to the topic and do
                  not constitute legal advice. The information contained herein is accurate at the
                  date of publication but please note that the law is ever changing and evolving.
                  If you require advice in relation to any matter raised in this article please
                  contact a member of the team.
                </p>
              </FadeIn>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <FadeIn delay={0.1}>
                {/* Author card */}
                <div className="brand-panel-pale border border-slate-200 p-6" data-testid="author-card">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                    Written by
                  </p>
                  {authorPerson ? (
                    <>
                      <img src={authorPerson.image} alt={authorPerson.name} className="mt-4 w-full object-cover" />
                      <p className="mt-4 text-lg font-medium text-brand-navy">{authorPerson.name}</p>
                      <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{authorPerson.role}</p>
                      <Link
                        to={`/team/${authorPerson.slug}`}
                        className="brand-text-link mt-4 inline-flex items-center gap-2"
                        data-testid="author-profile-link"
                      >
                        View profile <ArrowRight className="size-3.5" />
                      </Link>
                    </>
                  ) : (
                    <p className="mt-3 text-lg font-medium text-brand-navy">{article.author}</p>
                  )}
                </div>

                {/* Contact CTA */}
                <div className="brand-panel-dark mt-6 p-6">
                  <p className="text-lg font-light">Need advice on this topic?</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">
                    Speak to our team in confidence — our emergency line is answered 24 hours a day.
                  </p>
                  <div className="mt-4 space-y-2.5 text-sm">
                    <a href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`} className="brand-link-on-dark flex items-center gap-2.5" data-testid="article-contact-emergency-phone">
                      <Phone className="size-4 text-brand-terracotta" /> 24hr: {FIRM.emergencyPhone}
                    </a>
                    <a href={`mailto:${FIRM.email}`} className="brand-link-on-dark flex items-center gap-2.5" data-testid="article-contact-email">
                      <Mail className="size-4 text-brand-terracotta" /> {FIRM.email}
                    </a>
                  </div>
                  <Link
                    to="/contact"
                    className="brand-cta-primary mt-5 block py-3 text-center"
                    data-testid="article-contact-cta"
                  >
                    Get in touch
                  </Link>
                </div>

                {/* Related */}
                {related.length > 0 && (
                  <div className="mt-6 border border-slate-200 p-6">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                      More in {cat.title}
                    </p>
                    <ul className="mt-4 space-y-4">
                      {related.map((r) => (
                        <li key={r.slug}>
                          {r.body ? (
                            <Link
                              to={`/resources/${r.category}/${r.slug}`}
                              className="group block text-sm font-medium leading-snug text-slate-700 transition-colors hover:text-brand-terracotta"
                              data-testid={`related-article-${r.slug}`}
                            >
                              {r.title}
                            </Link>
                          ) : (
                            <a
                              href={r.externalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group block text-sm font-medium leading-snug text-slate-700 transition-colors hover:text-brand-terracotta"
                              data-testid={`related-article-${r.slug}`}
                            >
                              {r.title} <ExternalLink className="ml-1 inline size-3 text-slate-400" />
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </FadeIn>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
